import type { RouteLocationNormalizedLoadedGeneric } from "vue-router";
import type { RegistrationRequestDto } from "@ckweb/ckfetch/connectables/plantApi";
import { useAuthStore } from "../stores/auth";
import type { AuthFlow, PROVIDER_NAMES, UserModel } from "../types/providers";
import { getHygienaOptions } from "../utils/providers";
import { useForgotPassword, useRequestConfirmationEmail, useValidateConfirmationEmailToken } from "./user";

type LoginWithProviderOptions = {
  loginHint?: string;
  password?: string;
  providerName?: PROVIDER_NAMES;
};

export function useAuthApi(to?: RouteLocationNormalizedLoadedGeneric) {
  const $nuxt = useNuxtApp();
  const config = useRuntimeConfig();
  const authStore = useAuthStore();
  const { goToUnauthorizedRedirectPage, goToRoot } = useAuthQueryParams(to);
  const forgotPasswordMutate = useForgotPassword();
  const requestConfirmationEmailMutate = useRequestConfirmationEmail();
  const validateConfirmationEmailTokenMutate = useValidateConfirmationEmailToken();

  const currentUser = computed(() => authStore.currentUser);
  const displayUserName = computed(() => authStore.currentUser?.userName ?? authStore.currentAccountUsername);

  async function loginWithProvider(options: LoginWithProviderOptions = {}): Promise<UserModel | undefined> {
    await authStore.initialize(options.providerName);

    const loginOptions = options.loginHint
      ? {
          loginHint: options.loginHint,
          password: options.password,
        }
      : undefined;

    const account = await authStore.login(loginOptions);

    return account ? await authStore.refreshCurrentUser() : undefined;
  }

  async function attemptLogin(options: LoginWithProviderOptions = {}) {
    const result = await loginWithProvider(options);
    if (result) {
      return goToRoot();
    }
    return goToUnauthorizedRedirectPage();
  }

  async function resetCurrentUser() {
    const result = authStore.resetCurrentUser();
    if (result) {
      devConsole.info("📣 ckAuth: custom Nuxt Hook call ckauth:user-cleared");
      $nuxt.callHook("ckauth:user-cleared", {
        clearedUser: result,
      });
    }
    return result;
  }

  async function refreshCurrentUser() {
    const result = await authStore.refreshCurrentUser();
    if (result) {
      devConsole.info("📣 ckAuth: custom Nuxt Hook call ckauth:user-loaded");
      $nuxt.callHook("ckauth:user-loaded", {
        currentUser: result,
      });
    } else {
      devConsole.warn("⚠️ ckAuth: User load failed, custom Nuxt Hook call ckauth:user-load-failed");
      $nuxt.callHook("ckauth:user-load-failed");
    }
    return result;
  }

  async function requestForgotPasswordEmail(data: { email: string }) {
    const hygienaConfig = getHygienaOptions(config);
    return await forgotPasswordMutate.safeMutate({
      appName: hygienaConfig.appName,
      ...data,
    });
  }

  async function handleLogout() {
    await authStore.logout();
    if (!authStore.isAuthed) {
      await resetCurrentUser();
      devConsole.info("🔒 Auth logged out successfully.");
      // force rerun middlewares
      await reloadNuxtApp();
    }
  }

  async function withResetAuthFlow<T>(callback: (authFlow: AuthFlow) => Promise<T>): Promise<T> {
    try {
      return await callback($nuxt.$auth.authFlow);
    } finally {
      await authStore.resetAuthFlow();
    }
  }

  async function initAuthApi() {
    await authStore.refresh();
  }

  async function requestConfirmationEmail(payload: RegistrationRequestDto) {
    await requestConfirmationEmailMutate.safeMutate(payload);
    return !unref(requestConfirmationEmailMutate.errors);
  }

  return {
    authStore,
    currentUser,
    displayUserName,
    loginWithProvider,
    attemptLogin,
    initAuthApi,
    requestForgotPasswordEmail,
    refreshCurrentUser,
    requestConfirmationEmail,
    validateConfirmationEmailToken: validateConfirmationEmailTokenMutate.safeMutate,
    handleLogout,
    withResetAuthFlow,
    resetCurrentUser,
  };
}
